import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui';
import type { Assignee } from '@/lib/stock-types';
import {
  JOB_STAGES,
  boardToday,
  daysLate,
  dueLabel,
  isOverdue,
  type Job,
  type JobStage,
  type Machine,
} from '@/lib/data/jobs';
import { Callout } from '@/components/ui';
import { cn } from '@/lib/cn';
import { adminJobsQuery, jobBlanksQuery, meQuery, staffQuery, tenantQuery } from '@/queries';
import { JobCostPanel } from '@/components/admin/job-cost';

/**
 * The job board. Spec 002 US-7 / AC-7.2.
 *
 * Every live job in the shop on one screen, in the order it crosses the floor.
 * Cards drag between columns; the same transition is also available from the
 * job detail, and both write the same event.
 *
 * Drag uses the native HTML5 API rather than a library. The interaction is one
 * card onto one column, which does not justify the dependency.
 */

/** One colour per method, used on the card rail and in the legend. */
const METHOD_COLOUR: Record<string, string> = {
  embroidery: 'var(--color-ink)',
  screen: 'var(--color-brand)',
  dtf: '#3b82a8',
  sublimation: '#8b5e83',
  // Picking is not decoration, and the muted rail says so at a glance.
  stock_pick: 'var(--color-faint)',
};

const METHOD_LABEL: Record<string, string> = {
  embroidery: 'Embroidery',
  screen: 'Screen',
  dtf: 'DTF',
  sublimation: 'Sublimation',
  stock_pick: 'Pick and pack',
};

/**
 * Gate 2 on the card: are the blanks there?
 *
 * Only for a job that has not started. Once it is in production the question is
 * answered by the fact that it is running, and a warning on a card somebody is
 * already printing is noise.
 *
 * `useQuery`, not `useSuspenseQuery`: the board must draw before this answers,
 * and a card that will not appear until a stock check finishes is worse than a
 * card with no badge yet. R-202.
 */
function BlanksBadge({ job }: { job: Job }) {
  // Only before the press. Once it is printing, the question is answered by
  // the fact that it is running, and a warning on a card somebody is already
  // working on is noise.
  const before = job.stage === 'artwork';
  const { data } = useQuery({ ...jobBlanksQuery(job.ref), enabled: before });

  if (!before || !data || data.state === 'unknown' || data.state === 'ready') return null;

  const worst = data.short[0];
  return (
    <span
      className="inline-flex h-5 items-center gap-1 rounded-[4px] bg-warn-wash px-2 text-[10px] font-medium text-[color:var(--color-warn-ink)]"
      title={data.short
        .map((l) => `${l.code}: ${l.shortfall} short${l.on_order ? `, ${l.on_order} on order` : ''}`)
        .join('\n')}
    >
      {worst ? `${worst.shortfall} short` : 'short'}
      {worst?.expected_at ? ` \u00b7 ${worst.expected_at.slice(5)}` : ''}
    </span>
  );
}

function JobCard({
  job,
  machine,
  onDragStart,
  showCost,
  staff,
  onAssign,
  onCount,
  today,
  zone,
}: {
  job: Job;
  machine: Machine | undefined;
  onDragStart: (ref: string) => void;
  showCost: boolean;
  staff: Assignee[];
  onAssign: (ref: string, userId: string | null) => void;
  onCount: (ref: string, good: number | null) => void;
  /** The board's one idea of today, and the workshop's zone. See `jobs.ts`. */
  today: string;
  zone: string;
}) {
  const late = isOverdue(job, today, zone);
  return (
    <article
      draggable
      onDragStart={() => onDragStart(job.ref)}
      className={cn(
        // 14px padding, the sheet's phone rule, and it reads no worse on a
        // desktop. One shadow at rest and hover never resizes anything. R-189.
        'cursor-grab rounded-[10px] border border-line border-l-[3px] bg-panel p-[14px]',
        'shadow-[var(--shadow-1)] transition-colors duration-150 hover:bg-raised',
        'active:cursor-grabbing',
      )}
      style={{ borderLeftColor: METHOD_COLOUR[job.method_key] ?? 'var(--color-line-strong)' }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-[family-name:var(--font-mono)] text-[10.5px] font-medium tabular-nums text-muted">
          {job.ref}
        </span>
        {/* Late is the only red on this screen, which is what makes it read as
            urgent rather than as decoration. 1g. R-216. */}
        {late ? (
          <span className="rounded-[999px] bg-danger-wash px-[7px] py-[3px] text-[9.5px] font-medium text-[color:var(--color-danger-ink)]">
            Late {daysLate(job, today, zone)}d
          </span>
        ) : job.due_at ? (
          <span className="rounded-[999px] bg-raised px-[7px] py-[3px] text-[9.5px] font-medium text-muted">
            Due {dueLabel(job.due_at, zone)}
          </span>
        ) : (
          <span className="rounded-[999px] bg-raised px-[7px] py-[3px] font-[family-name:var(--font-mono)] text-[9px] font-medium uppercase tracking-[0.08em] text-faint">
            Undated
          </span>
        )}
      </div>

      <p className="mt-1.5 truncate text-[13px] font-semibold" title={job.customer}>
        {job.customer}
      </p>
      <p className="mt-0.5 truncate text-[11.5px] text-muted">{job.garment}</p>

      {/* Machine, operator, quantity, in that order, so reading down a column
          reads as a load list. 1g says so and it is right: the question at a
          board is "what is on that press today". */}
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-line-soft pt-2">
        <span className="truncate font-[family-name:var(--font-mono)] text-[10.5px] text-muted">
          {machine?.name ?? '\u2014'}
        </span>
        <span className="flex items-center gap-2">
          <select
            aria-label={`Who is doing ${job.ref}`}
            className="h-4 max-w-[92px] cursor-pointer truncate rounded-[4px] bg-transparent text-[10px] text-muted hover:bg-raised"
            value={job.assigned_to ?? ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onAssign(job.ref, e.target.value || null)}
          >
            <option value="">Unassigned</option>
            {staff.map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {person.full_name}
              </option>
            ))}
          </select>
          <span className="font-[family-name:var(--font-mono)] text-[10.5px] font-medium tabular-nums">
            {job.qty}
          </span>
        </span>
      </div>

      {(job.stage === 'qc' || job.stage === 'pack' || job.stage === 'complete') && (
        <label className="mt-2 flex items-center gap-2 text-[10.5px] text-muted">
          Good
          <input
            type="number"
            min={0}
            max={job.qty}
            defaultValue={job.units_good ?? ''}
            aria-label={`Good pieces from ${job.ref}`}
            className="h-6 w-14 rounded-[4px] border border-line bg-raised px-1.5 text-right font-[family-name:var(--font-mono)] text-[10.5px] tabular-nums text-ink"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => onCount(job.ref, e.target.value === '' ? null : Number(e.target.value))}
          />
          {job.units_good != null && job.units_good < job.qty && (
            <span className="text-[color:var(--color-warn-ink)]">
              {job.qty - job.units_good} lost
            </span>
          )}
        </label>
      )}

      <BlanksBadge job={job} />

      {/* Cost, for a caller entitled to it. 1g's card has no room for a panel,
          and it is right: a board answers "what is on that press today", not
          "what did it cost". So it is one line at the foot of the card, and the
          detail stays on the job. The route and the database both refuse a
          caller without the costs right, so this is presentation. R-216. */}
      {showCost && <JobCostPanel jobId={job.id} />}
    </article>
  );
}

export default function AdminJobsPage() {
  const { jobs: seeded, machines } = useSuspenseQuery(adminJobsQuery).data;
  // The server answers this, so the board cannot disagree with it about who is
  // internal and who may see a cost.
  const showCost = useSuspenseQuery(meQuery).data.isAdmin;
  const { staff } = useSuspenseQuery(staffQuery).data;
  // The workshop's own zone, not this browser's and not Sydney's (R-232).
  // Loaded by the root route, so it is already in the cache.
  const zone = useSuspenseQuery(tenantQuery).data.timezone;
  // Asked once for the whole board. Every card is judged against the same day,
  // so no card can disagree with its neighbour across midnight.
  const today = boardToday(zone);
  const qc = useQueryClient();

  /**
   * Giving a job to somebody.
   *
   * The server decides whether that person works here and whether the job is
   * still live: this only asks. On a refusal the board reloads rather than
   * keeping a name the database rejected, because a card that shows an owner
   * the system does not agree with is worse than one that shows none.
   */
  /** Gate 3. Recording how many survived the press. */
  async function count(ref: string, good: number | null) {
    const res = await fetch(`/api/jobs/${ref}/count`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ units_good: good }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      setMoveError(body?.error?.message ?? 'That count did not save.');
    }
    void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
  }

  async function assign(ref: string, userId: string | null) {
    const res = await fetch(`/api/jobs/${ref}/assignee`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ assigned_to: userId }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      setMoveError(body?.error?.message ?? 'That did not save.');
    }
    void qc.invalidateQueries({ queryKey: ['admin', 'jobs'] });
  }

  // Stage moves are SAVED. R-161.
  //
  // This used to be component state and nothing else: the card moved on screen,
  // nothing was written, and a refresh put it back. The comment here even said
  // "against Supabase this posts a guarded transition", and no such route
  // existed.
  //
  // The local map stays as the optimistic view, so the card moves under the
  // cursor at once, and it is reconciled by the re-read. A refused move puts
  // the card back and says why.
  const [moves, setMoves] = useState<Record<string, JobStage>>({});
  const [moveError, setMoveError] = useState<string | null>(null);
  const router = useRouter();
  const [dragging, setDragging] = useState<string | null>(null);
  const [machineFilter, setMachineFilter] = useState<string>('all');

  const jobs = useMemo(
    () => seeded.map((j) => (moves[j.ref] ? { ...j, stage: moves[j.ref]! } : j)),
    [seeded, moves],
  );

  const visible =
    machineFilter === 'all' ? jobs : jobs.filter((j) => j.machine_id === machineFilter);

  const machineById = useMemo(
    () => new Map(machines.map((m) => [m.id, m])),
    [machines],
  );

  const overdue = jobs.filter((j) => isOverdue(j, today, zone)).length;

  return (
    <div>
      {/* A refused move must say so. The card going back on its own, with no
          explanation, is the same silence this whole screen had. R-161. */}
      {moveError && (
        <div className="mb-4">
          <Callout tone="danger" title={moveError} />
        </div>
      )}
      {/* The header block from 1l: breadcrumb 11px, title 22px/600, meta
          12.5px, and at most two actions. R-216. */}
      <div className="mb-[14px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] text-muted">Production</p>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Job board</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            {jobs.filter((j) => j.stage !== 'complete').length} open jobs
            {' · '}
            {/* The loudest thing on this screen, deliberately. It is the only
                red on it. */}
            {overdue > 0 ? (
              <span className="font-medium text-[color:var(--color-danger-ink)]">
                {overdue} late
              </span>
            ) : (
              <span>nothing late</span>
            )}
            {' · '}
            {machines.length} machines
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setMachineFilter('all')}
            className={cn(
              'inline-flex h-10 items-center rounded-[10px] border px-3 text-sm font-semibold',
              machineFilter === 'all'
                ? 'border-ink bg-ink text-panel'
                : 'border-line-strong bg-panel hover:border-ink/40',
            )}
          >
            All machines
          </button>
          {machines.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMachineFilter(m.id)}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-sm font-semibold',
                machineFilter === m.id
                  ? 'border-ink bg-ink text-panel'
                  : 'border-line-strong bg-panel hover:border-ink/40',
              )}
            >
              {m.name}
              {m.state === 'maintenance' && (
                <span className="font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-[color:var(--color-warn-ink)]">
            Maint
          </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Where work comes from, said on the screen that has none of it.
          Moving an order to "In production" does not make a job: a sale from
          STOCK is picked and packed, and only a decorated line is work. Two
          people have now looked for a card that was never going to be here,
          which makes it a defect in the screen and not in their expectation.
          R-196. */}
      {jobs.length === 0 && (
        <div className="mb-5 rounded-[10px] border border-dashed border-line px-6 py-10 text-center">
          <p className="text-[15px] font-semibold">Nothing to make yet</p>
          <p className="mx-auto mt-2 max-w-[62ch] text-[13px] text-muted">
            A job appears here when a sale needs decorating: a print-on-demand
            line in the shop, or a quote converted to an order. An order of
            garments sold from stock is picked and packed, so moving it to
            &ldquo;In production&rdquo; does not create work here.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-5">
        {JOB_STAGES.map((stage) => {
          const column = visible.filter((j) => j.stage === stage.key);
          return (
            <section
              key={stage.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragging) {
                  const ref = dragging;
                  setMoves((m) => ({ ...m, [ref]: stage.key }));
                  setMoveError(null);
                  void (async () => {
                    try {
                      const res = await fetch(`/api/jobs/${encodeURIComponent(ref)}/stage`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'same-origin',
                        body: JSON.stringify({ stage: stage.key }),
                      });
                      if (!res.ok) {
                        const body = (await res.json().catch(() => null)) as
                          | { error?: { message?: string } } | null;
                        setMoveError(body?.error?.message ?? 'That move was refused.');
                        // Put it back rather than leave a card somewhere the
                        // studio is not.
                        setMoves((m) => {
                          const next = { ...m };
                          delete next[ref];
                          return next;
                        });
                        return;
                      }
                      await router.invalidate();
                    } catch {
                      setMoveError('That move did not save. Check your connection.');
                      setMoves((m) => {
                        const next = { ...m };
                        delete next[ref];
                        return next;
                      });
                    }
                  })();
                }
                setDragging(null);
              }}
              className="min-h-[240px] rounded-[10px] bg-raised p-2"
            >
              {/* The count sits at the right of the heading, as 1g draws it,
                  so a glance down the row of headings reads as a load. */}
              <header className="mb-2.5 flex items-baseline justify-between gap-2 border-b border-line-strong px-1 pb-2">
                <span>
                  <h2 className="font-[family-name:var(--font-mono)] text-[11px] font-semibold uppercase leading-tight tracking-[0.07em]">
                    {stage.label}
                  </h2>
                  <p className="mt-1 text-[11px] text-muted">{stage.hint}</p>
                </span>
                <span className="font-[family-name:var(--font-mono)] text-[10.5px] tabular-nums text-faint">
                  {column.length}
                </span>
              </header>

              <div className="flex flex-col gap-2">
                {/* An empty stage keeps its column and says what fills it. A
                    blank column reads as a fault; a sentence reads as a state. */}
                {column.length === 0 && (
                  <p className="rounded-[10px] border border-dashed border-line px-3 py-6 text-center text-[11px] leading-[1.5] text-muted">
                    {stage.empty}
                  </p>
                )}
                {column.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    machine={job.machine_id ? machineById.get(job.machine_id) : undefined}
                    onDragStart={setDragging}
                    showCost={showCost}
                    staff={staff}
                    onAssign={assign}
                    onCount={count}
                    today={today}
                    zone={zone}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-5 text-[13px] text-muted">
        {Object.entries(METHOD_LABEL).map(([key, label]) => (
          <span key={key} className="flex items-center gap-2">
            <i
              aria-hidden
              className="block h-1 w-4 rounded-full"
              style={{ background: METHOD_COLOUR[key] }}
            />
            {label}
          </span>
        ))}
        <span className="text-faint">Drag a card between columns to move the job.</span>
      </div>
    </div>
  );
}
